import {
  Alert,
  Anchor,
  Button,
  Group,
  MultiSelect,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconBuilding, IconClipboardCheck, IconShieldCheck, IconUsers } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthIdentityChooser } from "../../components/AuthIdentityChooser";
import { CitySelect } from "../../components/CitySelect";
import { AppleJoinButton } from "../../components/AppleJoinButton";
import { GoogleJoinButton } from "../../components/GoogleJoinButton";
import { OnboardingSection, OnboardingShell } from "../../components/OnboardingShell";
import { PhoneInput } from "../../components/PhoneInput";
import { renderSegmentOption } from "../../components/SegmentOptionLabel";
import { TermsAcceptancePanel } from "../../components/TermsAcceptancePanel";
import { isTermsComplete } from "../../lib/termsAcceptance";
import { clientTerms, consultancyTerms, TERMS_VERSION } from "../../constants/platformTerms";
import { useAuth } from "../../context/useAuth";
import { JOIN_ORG_HUB_COPY, joinLandingPath, ORG_KYC_COPY } from "../../lib/companyKyc";
import { AUTH_IDENTITY_COPY } from "../../lib/kyc";
import { catalogFuzzyFilter } from "../../lib/catalogFuzzyFilter";
import { authApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { clearSignupSession, getSignupSession, hasSignupSession } from "../../lib/googleSignupSession";
import { validateIndianPhoneField, toPhoneForApi } from "../../lib/phone";
import { SEGMENT_SELECT_COPY } from "../../lib/segments";
import { CITY_SELECT_COPY } from "../../lib/cities";
import {
  REGISTRATION_PASSWORD_COPY,
  validatePasswordConfirmation,
} from "../../constants/account";
import { usePublicSegmentCatalog } from "../../lib/useSegmentCatalog";
import type { CompanyType, Segment } from "../../types/api";
import "../../components/authAnimations.css";
import { axioTokens } from "../../theme";

const segmentActionLinkStyle = {
  fontFamily: "var(--axio-font-head)",
  letterSpacing: "var(--axio-letter-spacing-snug)",
};

export function JoinCompanyPage({ companyType }: { companyType: CompanyType }) {
  const { applyTokenUser } = useAuth();
  const navigate = useNavigate();
  const isConsultancy = companyType === "consultancy";
  const copy = isConsultancy ? JOIN_ORG_HUB_COPY.consultancy : JOIN_ORG_HUB_COPY.client;
  const terms = isConsultancy ? consultancyTerms : clientTerms;
  const termsRole = isConsultancy ? "consultancy" : "client";
  const { flatOptions: segmentOptions } = usePublicSegmentCatalog();

  const [googleConnected, setGoogleConnected] = useState(hasSignupSession());
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [masterAccepted, setMasterAccepted] = useState(false);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [termsVersion, setTermsVersion] = useState(TERMS_VERSION);
  const [segments, setSegments] = useState<string[]>([]);
  const [segmentsError, setSegmentsError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      full_name: "",
      email: "",
      phone: "",
      password: "",
      confirm_password: "",
      legal_name: "",
      display_name: "",
      website: "",
      industry: "",
      city: "",
    },
    validate: {
      full_name: (v) => (v.trim().length >= 2 ? null : "Enter your full name"),
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : "Enter a valid email"),
      phone: validateIndianPhoneField,
      password: (v) => (v.length >= 8 ? null : "Password must be at least 8 characters"),
      confirm_password: (v, values) => validatePasswordConfirmation(values.password, v),
      legal_name: (v) => (v.trim().length >= 2 ? null : "Enter the legal name"),
      city: (v) => (!isConsultancy || v.trim() ? null : CITY_SELECT_COPY.requiredError),
    },
  });

  useEffect(() => {
    authApi.joinTerms(termsRole).then((res) => setTermsVersion(res.version)).catch(() => {});
  }, [termsRole]);

  const termsOk = isTermsComplete(terms, masterAccepted);
  const landing = joinLandingPath(companyType);
  const segmentsOk = !isConsultancy || segments.length > 0;
  const cityOk = !isConsultancy || Boolean(form.values.city.trim());
  const consultancyDetailsOk = segmentsOk && cityOk;
  const allSegmentValues = segmentOptions.map((option) => option.value);

  function setHireSegments(value: string[]) {
    setSegments(value);
    setSegmentsError(value.length === 0 ? SEGMENT_SELECT_COPY.requiredError : null);
  }

  async function submitEmail() {
    const validation = form.validate();
    if (validation.hasErrors || !termsOk || !consultancyDetailsOk) {
      if (!segmentsOk) setSegmentsError(SEGMENT_SELECT_COPY.requiredError);
      return;
    }
    setEmailSubmitting(true);
    try {
      const res = await authApi.registerCompany({
        full_name: form.values.full_name.trim(),
        email: form.values.email.trim(),
        phone: toPhoneForApi(form.values.phone),
        password: form.values.password,
        company_type: companyType,
        legal_name: form.values.legal_name.trim(),
        display_name: form.values.display_name.trim() || undefined,
        website: form.values.website.trim() || undefined,
        industry: form.values.industry.trim() || undefined,
        city: form.values.city.trim() || undefined,
        ...(isConsultancy ? { segments: segments as Segment[] } : {}),
        terms_version: termsVersion,
        terms_accepted: true,
      });
      applyTokenUser(res.access_token, res.refresh_token, res.user);
      notifications.show({
        color: "green",
        title: "Welcome",
        message: ORG_KYC_COPY.subtitle,
      });
      navigate(landing);
    } catch (err) {
      notifications.show({
        color: "red",
        title: "Could not register",
        message: err instanceof ApiError ? err.message : "Something went wrong",
      });
    } finally {
      setEmailSubmitting(false);
    }
  }

  async function submitGoogle() {
    const session = getSignupSession();
    if (!session) {
      notifications.show({
        color: "yellow",
        title: "Connect Apple or Google first",
        message: "Use Continue with Apple or Continue with Google above before submitting.",
      });
      return;
    }
    if (form.values.legal_name.trim().length < 2 || !termsOk || !consultancyDetailsOk) {
      if (!segmentsOk) setSegmentsError(SEGMENT_SELECT_COPY.requiredError);
      if (!cityOk) form.setFieldError("city", CITY_SELECT_COPY.requiredError);
      return;
    }
    setGoogleSubmitting(true);
    setTokenError(null);
    try {
      const completePayload = {
        requested_role: "company_user" as const,
        create_company: true,
        company_type: companyType,
        legal_name: form.values.legal_name.trim(),
        display_name: form.values.display_name.trim() || undefined,
        website: form.values.website.trim() || undefined,
        industry: form.values.industry.trim() || undefined,
        city: form.values.city.trim() || undefined,
        ...(isConsultancy ? { segments: segments as Segment[] } : {}),
        terms_version: termsVersion,
        terms_accepted: true,
      };
      const res =
        session.provider === "apple"
          ? await authApi.appleComplete({ ...completePayload, apple_signup_token: session.token })
          : await authApi.googleComplete({ ...completePayload, google_signup_token: session.token });
      clearSignupSession();
      if (res.access_token && res.user) {
        applyTokenUser(res.access_token, res.refresh_token ?? "", res.user);
        navigate(landing);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === "invalid_signup_token") {
        setTokenError(err.message);
        clearSignupSession();
        setGoogleConnected(false);
      } else {
        notifications.show({
          color: "red",
          title: "Could not register",
          message: err instanceof ApiError ? err.message : "Something went wrong",
        });
      }
    } finally {
      setGoogleSubmitting(false);
    }
  }

  return (
    <OnboardingShell
      role="hr"
      accent="blue"
      heroTitle={
        <>
          {copy.title}.
          <br />
          <span className="gradientTextBlue">{copy.subtitle}</span>
        </>
      }
      heroSubtitle={copy.description}
      highlights={[
        { icon: IconBuilding, text: copy.features[0] },
        { icon: IconUsers, text: copy.features[1] },
        { icon: IconClipboardCheck, text: copy.features[2] },
        { icon: IconShieldCheck, text: ORG_KYC_COPY.verifyHighlight },
      ]}
    >
      <Stack gap="var(--axio-unit-32)">
        <div className="staggerEnter" style={{ animationDelay: "var(--axio-duration-quick)" }}>
          <Title order={3} fw="var(--axio-font-weight-extra-bold)" style={{ fontFamily: "var(--axio-font-head)" }}>
            {copy.title}
          </Title>
          <Text size="sm" c="dimmed" mt="var(--axio-unit-6)">
            {ORG_KYC_COPY.subtitle}
          </Text>
        </div>

        {tokenError && (
          <Alert color="red" variant="light" title="Session expired">
            {tokenError}
          </Alert>
        )}

        <OnboardingSection
          title={AUTH_IDENTITY_COPY.sectionTitle}
          description={AUTH_IDENTITY_COPY.joinDescription}
          accent="blue"
        >
          {googleConnected ? (
            <Alert
              color="blue"
              variant="light"
              title={
                getSignupSession()?.provider === "apple"
                  ? AUTH_IDENTITY_COPY.appleConnectedTitle
                  : AUTH_IDENTITY_COPY.googleConnectedTitle
              }
              icon={<IconShieldCheck size="var(--axio-unit-18)" />}
              styles={{
                root: {
                  background: axioTokens.blueAlpha06,
                  borderColor: axioTokens.blueAlpha15,
                },
              }}
            >
              {getSignupSession()?.provider === "apple"
                ? AUTH_IDENTITY_COPY.appleConnectedBody
                : AUTH_IDENTITY_COPY.googleConnectedBody}
            </Alert>
          ) : (
            <AuthIdentityChooser
              appleSlot={
                <AppleJoinButton
                  returnPath={companyType === "consultancy" ? "/join/consultancy" : "/join/company"}
                />
              }
              googleSlot={
                <GoogleJoinButton
                  onConnected={() => {
                    setGoogleConnected(true);
                    setEmailFormOpen(false);
                  }}
                />
              }
              emailOpen={emailFormOpen}
              onChooseEmail={() => setEmailFormOpen(true)}
              emailFields={
                <Stack gap="md">
                  <TextInput label="Full name" withAsterisk {...form.getInputProps("full_name")} />
                  <TextInput label="Email" type="email" withAsterisk {...form.getInputProps("email")} />
                  <PhoneInput
                    label="Phone"
                    required
                    value={form.values.phone}
                    onChange={(value) => form.setFieldValue("phone", value)}
                    error={form.errors.phone}
                  />
                  <PasswordInput
                    label={REGISTRATION_PASSWORD_COPY.passwordLabel}
                    withAsterisk
                    {...form.getInputProps("password")}
                    styles={{ visibilityToggle: { display: "none" } }}
                  />
                  <PasswordInput
                    label={REGISTRATION_PASSWORD_COPY.confirmPasswordLabel}
                    withAsterisk
                    {...form.getInputProps("confirm_password")}
                  />
                </Stack>
              }
            />
          )}
        </OnboardingSection>

        <OnboardingSection title="Company details" description={ORG_KYC_COPY.detailsDescription} accent="blue">
          <Stack gap="md">
            <TextInput label="Legal name" withAsterisk {...form.getInputProps("legal_name")} />
            <TextInput label="Display name" {...form.getInputProps("display_name")} />
            <TextInput label="Website" {...form.getInputProps("website")} />
            <TextInput label="Industry" {...form.getInputProps("industry")} />
            <CitySelect
              label={CITY_SELECT_COPY.label}
              publicCatalog
              withAsterisk={isConsultancy}
              clearable={!isConsultancy}
              value={form.values.city}
              onChange={(city) => form.setFieldValue("city", city)}
              error={form.errors.city}
            />
          </Stack>
        </OnboardingSection>

        {isConsultancy && (
          <OnboardingSection
            title={SEGMENT_SELECT_COPY.hireForLabel}
            description={SEGMENT_SELECT_COPY.hireForDescription}
            accent="blue"
          >
            <Stack gap="xs">
              <MultiSelect
                label={SEGMENT_SELECT_COPY.hireForLabel}
                withAsterisk
                searchable
                clearable
                data={segmentOptions}
                renderOption={renderSegmentOption}
                filter={catalogFuzzyFilter}
                nothingFoundMessage={SEGMENT_SELECT_COPY.nothingFound}
                value={segments}
                onChange={setHireSegments}
                error={segmentsError}
              />
              <Group gap="xs">
                <Anchor
                  href="#select-all-segments"
                  size="sm"
                  underline="hover"
                  fw="var(--axio-font-weight-semibold)"
                  c="var(--axio-color-ink)"
                  style={segmentActionLinkStyle}
                  onClick={(event) => {
                    event.preventDefault();
                    setHireSegments(allSegmentValues);
                  }}
                >
                  {SEGMENT_SELECT_COPY.selectAll}
                </Anchor>
                <Anchor
                  href="#clear-segments"
                  size="sm"
                  underline="hover"
                  fw="var(--axio-font-weight-semibold)"
                  c="var(--axio-color-muted)"
                  style={segmentActionLinkStyle}
                  onClick={(event) => {
                    event.preventDefault();
                    setHireSegments([]);
                  }}
                >
                  {SEGMENT_SELECT_COPY.clearAll}
                </Anchor>
              </Group>
            </Stack>
          </OnboardingSection>
        )}

        <OnboardingSection title="Platform terms" description="Read and accept to continue." accent="blue">
          <TermsAcceptancePanel
            terms={terms}
            masterAccepted={masterAccepted}
            onMasterAcceptedChange={setMasterAccepted}
            version={termsVersion}
            accent="blue"
          />
        </OnboardingSection>

        {!termsOk && (googleConnected || emailFormOpen) && (
          <Alert color="yellow" variant="light" icon={<IconShieldCheck size="var(--axio-unit-18)" />}>
            Accept the terms above before creating your account.
          </Alert>
        )}

        {googleConnected && (
          <Button
            className="ctaGlowBlue"
            color="blue"
            size="md"
            fullWidth
            loading={googleSubmitting}
            disabled={!termsOk || !consultancyDetailsOk || form.values.legal_name.trim().length < 2}
            onClick={() => void submitGoogle()}
            style={{ height: "var(--axio-unit-48)", fontSize: "var(--axio-font-size-control)", fontWeight: "var(--axio-font-weight-semibold)" }}
          >
            Create {isConsultancy ? "consultancy" : "company"} account
          </Button>
        )}

        {emailFormOpen && !googleConnected && (
          <Button
            className="ctaGlowBlue"
            color="blue"
            size="md"
            fullWidth
            loading={emailSubmitting}
            disabled={!termsOk || !consultancyDetailsOk}
            onClick={() => void submitEmail()}
            style={{ height: "var(--axio-unit-48)", fontSize: "var(--axio-font-size-control)", fontWeight: "var(--axio-font-weight-semibold)" }}
          >
            Create {isConsultancy ? "consultancy" : "company"} account
          </Button>
        )}
      </Stack>
    </OnboardingShell>
  );
}
